import { useEffect, useState } from "react";
import ICalculatorConfigurationWithMinimumFee from "./@shared/models/ICalculatorConfigurationWithMinimumFee";
import { ICalculatorInput } from "./@shared/models/ICalculatorInput";
import ColourModeSelector from "./components/ColourModeSelector";
import DuplexModeSelector from "./components/DuplexModeSelector";
import PaperSizesSelector from "./components/PaperSizesSelector";
import PaperTypeSelector from "./components/PaperTypeSelector";
import FinishingOptionsSelector from "./components/FinishingOptionsSelector";
import AddToCart from "./components/AddToCart";
import IAddToCartModel from "./models/IAddToCartModel";
import NumberOfCopiesSelector from "./components/NumberOfCopiesSelector";
import CalculatePrice from "./@shared/lib/CalculatePrice";
import FileInputSelector from "./components/FileInputSelector";
import { IPaperSize } from "./@shared/lib/PaperSizesUtils";
import PagesInDocument from "./components/PagesInDocument";
import filterPaperSizes from "./@shared/lib/filterHelpers/filterPaperSizes";
import filterPaperTypes from "./@shared/lib/filterHelpers/filterPaperTypes";
import filterDuplexOptions from "./@shared/lib/filterHelpers/filterDuplexOptions";
import filterInnerPaperTypes from "./@shared/lib/filterHelpers/filterInnerPaperTypes";
import filterColourModes from "./@shared/lib/filterHelpers/filterColourModes";
import filterFinishingOptions from "./@shared/lib/filterHelpers/filterFinishingOptions";
import validateCalculatorInput from "./@shared/lib/validators/validateCalculatorInput";
import { ICalculatorOutput } from "./@shared/models/ICalculatorOutput";
import MixModePagesSelector from "./components/MixModePagesSelector";
import ValidationErrorMessage from "./components/ValidationErrorMessage";

interface IProps {
  configuration: ICalculatorConfigurationWithMinimumFee;
  printingOption: string;
  productId: number;
  userType: string;
  wpNonce: string;
}

function CalculatorInputs({
  configuration,
  printingOption,
  productId,
  userType,
  wpNonce,
}: IProps) {
  const [numberOfPages, setNumberOfPages] = useState(0);

  const printingOptionDetails =
    configuration.pricingConfiguration.printingOptions.filter(
      (po) => po.code === printingOption,
    )[0];

  const [calculatorInput, setCalculatorInput] = useState({
    userType: userType ?? "individual",
    selectedPrintingOption: printingOption,
    selectedPaperSize: "",
    selectedPaperType: "",
    isDuplexSelected: false,
    pagesBlack: 0,
    pagesColour: 0,
    numCopies: 1,
    selectedMode: "colour",
    selectedFinishingOption: "",
  } as ICalculatorInput);

  const [touched, setTouched] = useState({
    userType: false,
    selectedPrintingOption: false,
    selectedPaperSize: false,
    selectedPaperType: false,
    isDuplexSelected: false,
    pagesBlack: false,
    pagesColour: false,
    numCopies: false,
    selectedMode: false,
    selectedFinishingOption: false,
    numPages: false,
    selectedCoverMode: false,
    selectedCoverPaperType: false,
  });

  const [fileContent, setFileContent] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [blackPageRange, setBlackPageRange] = useState("");
  const [colourPageRange, setColourPageRange] = useState("");

  useEffect(() => {
    setCalculatorInput((prev) => {
      const newInputs = { ...prev };
      newInputs.userType = userType;
      return newInputs;
    });
  }, [userType]);

  const inputValidation = validateCalculatorInput(
    calculatorInput,
    configuration,
  );

  const filteredPaperSizes = filterPaperSizes(calculatorInput, configuration);
  const filteredCoverPaperTypes = filterPaperTypes(
    configuration,
    calculatorInput,
  );
  const filteredInnerPaperTypes = filterInnerPaperTypes(
    configuration,
    calculatorInput,
  );
  const filteredDuplexOptions = filterDuplexOptions(
    configuration,
    calculatorInput,
  );
  const filteredColourModes = filterColourModes(
    calculatorInput,
    configuration.pricingConfiguration,
  );
  const filteredFinishingOptions = filterFinishingOptions(
    configuration,
    calculatorInput,
  );
  function handlePaperSizeChanged(paperSize: string) {
    setCalculatorInput((prev) => {
      return {
        ...prev,
        selectedPaperSize: paperSize,
        selectedPaperType: "",
        selectedFinishingOption: "",
      };
    });
    setTouched((prev) => {
      return {
        ...prev,
        selectedPaperSize: true,
        selectedPaperType: false,
        selectedFinishingOption: false,
      };
    });
  }

  function handlePaperTypeChanged(paperType: string) {
    setCalculatorInput((prev) => {
      return {
        ...prev,
        selectedPaperType: paperType,
        selectedFinishingOption: "",
      };
    });
    setTouched((prev) => {
      return {
        ...prev,
        selectedPaperType: true,
        selectedFinishingOption: false,
      };
    });
  }

  function handleCoverPaperTypeChanged(paperType: string) {
    setCalculatorInput((prev) => {
      return {
        ...prev,
        selectedCoverPaperType: paperType,
      };
    });
    setTouched((prev) => {
      return { ...prev, selectedCoverPaperType: true };
    });
  }

  function handleDuplexModeChanged(duplexMode: boolean) {
    setCalculatorInput((prev) => {
      return { ...prev, isDuplexSelected: duplexMode };
    });
    setTouched((prev) => {
      return { ...prev, isDuplexSelected: true };
    });
  }

  function handleColourModeChanged(colourMode: string) {
    setBlackPageRange("");
    setColourPageRange("");
    updateNumberOfPages(colourMode, numberOfPages);
  }

  function updateNumberOfPages(colourMode: string, numberOfPages: number) {
    let pagesBlack = colourMode === "black" ? numberOfPages : 0;
    let pagesColour = colourMode === "colour" ? numberOfPages : 0;
    setCalculatorInput((prev) => {
      if (colourMode === "mix") {
        return {
          ...prev,
          selectedMode: colourMode,
          pagesBlack: 0,
          pagesColour: numberOfPages,
        };
      }
      return {
        ...prev,
        selectedMode: colourMode,
        pagesBlack: pagesBlack,
        pagesColour: pagesColour,
      };
    });
    setTouched((prev) => {
      return {
        ...prev,
        selectedMode: true,
        pagesBlack: true,
        pagesColour: true,
        numPages: true,
      };
    });
  }

  function handleCoverColourModeChanged(colourMode: string) {
    setCalculatorInput((prev) => {
      return { ...prev, selectedCoverMode: colourMode };
    });
    setTouched((prev) => {
      return { ...prev, selectedCoverMode: true };
    });
  }

  function handleFinishingOptionChanged(finishingOption: string) {
    setCalculatorInput((prev) => {
      return { ...prev, selectedFinishingOption: finishingOption };
    });
    setTouched((prev) => {
      return { ...prev, selectedFinishingOption: true };
    });
  }

  function handleQuantityChanged(quantity: number) {
    setCalculatorInput((prev) => {
      return { ...prev, numCopies: quantity };
    });
    setTouched((prev) => {
      return { ...prev, quantity: true };
    });
  }

  async function addToCart() {
    setSubmitting(true);
    if (fileContent !== null) {
      const formData = new FormData();
      formData.append("file", fileContent);

      await fetch("/?rest_route=/copyquick/printondemand/uploadFile", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      }).then(async (r) => {
        if (r.status === 200) {
          // for some reason we're getting a string back from the server, not json
          const responseJson = JSON.parse(await r.json());
          await completeAddToCart(responseJson.fileId);
        } else {
          setSubmitting(false);
        }
      });
    }

    async function completeAddToCart(fileId: string) {
      const addToCartModel = {
        ...calculatorInput,
        productId,
        configurationId: configuration.uID,
        fileId,
        blackPageRange:
          calculatorInput.selectedMode === "mix" ? blackPageRange : "",
        colourPageRange:
          calculatorInput.selectedMode === "mix" ? colourPageRange : "",
        fileName: fileContent?.name ?? "",
      } as IAddToCartModel;
      await fetch("/?rest_route=/copyquick/printondemand/addToCart", {
        method: "POST",
        headers: { "X-WP-Nonce": wpNonce },
        body: JSON.stringify(addToCartModel),
      })
        .then(async (r) => {
          if (r.status === 200) {
            // reload current page
            window.location.reload();
          }
        })
        .catch((e) => {});
    }
  }

  const calculatorOutput = inputValidation.isValid
    ? CalculatePrice(calculatorInput, configuration, configuration.minimumFee)
    : ({
        hasPrice: false,
        errorMessage: "Input Validation Failed",
        grandTotal: 0,
        printingPriceDetails: [],
      } as ICalculatorOutput);

  function handleFileContentUpdated(file: File) {
    setFileContent(file);
  }

  function handleFileUpdated(
    numberOfPages: number,
    paperSize: IPaperSize | null,
  ): void {
    setNumberOfPages(numberOfPages);
    updateNumberOfPages(calculatorInput.selectedMode, numberOfPages);
    if (paperSize) {
      handlePaperSizeChanged(paperSize.code);
    }
  }

  function handleMixModeQuantityChange(
    blackQuantity: number,
    colourQuantity: number,
    blackPageRange: string,
    colourPageRange: string,
  ) {
    setCalculatorInput((prev) => {
      return {
        ...prev,
        pagesBlack: blackQuantity,
        pagesColour: colourQuantity,
      };
    });

    setBlackPageRange(blackPageRange);
    setColourPageRange(colourPageRange);
  }

  return (
    <div>
      <table className="variations" cellSpacing="0" role="presentation">
        <tbody>
          <FileInputSelector
            onFileContentUpdated={handleFileContentUpdated}
            onFileUpdated={handleFileUpdated}
            requiresMultiplesOf4Mode={
              printingOptionDetails.requireMultiplesOfFour
            }
            availablePaperSizes={filteredPaperSizes}
            selectedPaperSize={calculatorInput.selectedPaperSize}
          />
          <PagesInDocument numberOfPages={numberOfPages} />
          <ValidationErrorMessage
            touched={touched.numPages}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "numPages")
              .map((m) => m.message)}
          />
          <PaperSizesSelector
            paperSizes={filteredPaperSizes}
            selectedPaperSize={calculatorInput.selectedPaperSize}
            onPaperSizeChanged={handlePaperSizeChanged}
          />
          <ValidationErrorMessage
            touched={touched.selectedPaperSize}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "selectedPaperSize")
              .map((m) => m.message)}
          />
          <DuplexModeSelector
            duplexModes={filteredDuplexOptions}
            selectedDuplexMode={calculatorInput.isDuplexSelected}
            onDuplexModeChanged={handleDuplexModeChanged}
          />
          <ValidationErrorMessage
            touched={touched.isDuplexSelected}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "isDuplexSelected")
              .map((m) => m.message)}
          />
          <PaperTypeSelector
            labelPrefix={printingOptionDetails.hasCover ? "Inner" : ""}
            paperTypes={filteredInnerPaperTypes}
            selectedPaperType={calculatorInput.selectedPaperType}
            onPaperTypeChanged={handlePaperTypeChanged}
          />
          <ValidationErrorMessage
            touched={touched.selectedPaperType}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "selectedPaperType")
              .map((m) => m.message)}
          />
          <ColourModeSelector
            labelPrefix={printingOptionDetails.hasCover ? "Inner" : ""}
            onColourModeChanged={handleColourModeChanged}
            selectedColourMode={calculatorInput.selectedMode}
            colourModes={filteredColourModes}
          />
          <ValidationErrorMessage
            touched={touched.selectedMode}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "selectedMode")
              .map((m) => m.message)}
          />
          {calculatorInput.selectedMode === "mix" ? (
            <MixModePagesSelector
              totalPages={numberOfPages}
              onQuantityChanged={handleMixModeQuantityChange}
            />
          ) : null}
          {printingOptionDetails.hasCover ? (
            <>
              <PaperTypeSelector
                labelPrefix="Cover"
                paperTypes={filteredCoverPaperTypes}
                selectedPaperType={calculatorInput.selectedCoverPaperType}
                onPaperTypeChanged={handleCoverPaperTypeChanged}
              />
              <ValidationErrorMessage
                touched={touched.selectedCoverPaperType}
                validationMessages={inputValidation.errorMessages
                  .filter((p) => p.propertyName === "selectedCoverPaperType")
                  .map((m) => m.message)}
              />
              <ColourModeSelector
                labelPrefix="Cover"
                onColourModeChanged={handleCoverColourModeChanged}
                selectedColourMode={calculatorInput.selectedCoverMode}
                colourModes={filteredColourModes}
              />
              <ValidationErrorMessage
                touched={touched.selectedCoverMode}
                validationMessages={inputValidation.errorMessages
                  .filter((p) => p.propertyName === "selectedCoverMode")
                  .map((m) => m.message)}
              />
            </>
          ) : null}
          <FinishingOptionsSelector
            selectedFinishingOptionId={calculatorInput.selectedFinishingOption}
            finishingOptions={filteredFinishingOptions}
            onFinishingOptionChanged={handleFinishingOptionChanged}
          />
          <ValidationErrorMessage
            touched={touched.selectedFinishingOption}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "selectedFinishingOption")
              .map((m) => m.message)}
          />
          <NumberOfCopiesSelector
            quantity={calculatorInput.numCopies}
            onQuantityChanged={handleQuantityChanged}
          />
          <ValidationErrorMessage
            touched={touched.numCopies}
            validationMessages={inputValidation.errorMessages
              .filter((p) => p.propertyName === "numCopies")
              .map((m) => m.message)}
          />
          <AddToCart
            inputValidation={inputValidation}
            calculatorInput={calculatorInput}
            calculatorOutput={calculatorOutput}
            onAddToCartClicked={addToCart}
            submitting={submitting}
          />
        </tbody>
      </table>
    </div>
  );
}

export default CalculatorInputs;
